import { PriceListAction } from 'models/action';
import { PriceListMaterial } from 'models/material';
import { ActionPrice } from 'models/organization';
import { OrderBy, OrderDirection } from 'models/search-result';

type TextSelector<Row> = (row: Row) => string | undefined;

// Swedish, case-insensitive, natural ordering: F109 < F109b < F110 and Å/Ä/Ö after Z.
const naturalCollator = new Intl.Collator('sv', { numeric: true, sensitivity: 'base' });

// Surrounding whitespace is ignored and blank values sort last in either direction.
const compareText = (first: string | undefined, second: string | undefined, direction: OrderDirection): number => {
    const trimmedFirst = first?.trim() ?? '';
    const trimmedSecond = second?.trim() ?? '';
    if (!trimmedFirst || !trimmedSecond) {
        return Number(!trimmedFirst) - Number(!trimmedSecond);
    }
    const comparison = naturalCollator.compare(trimmedFirst, trimmedSecond);
    return direction === OrderDirection.desc ? -comparison : comparison;
};

// The first selector is the clicked column and follows the chosen direction; the rest
// are tie-breakers that always run ascending so equal rows keep a predictable order.
const sortRows = <Row>(rows: Row[], selectors: TextSelector<Row>[], direction: OrderDirection): Row[] =>
    [...rows].sort((firstRow, secondRow) =>
        selectors.reduce(
            (comparison, selectText, selectorIndex) =>
                comparison ||
                compareText(
                    selectText(firstRow),
                    selectText(secondRow),
                    selectorIndex === 0 ? direction : OrderDirection.asc,
                ),
            0,
        ),
    );

export const toggleOrderBy = (currentOrderBy: OrderBy, field: string): OrderBy => {
    const isActiveColumnAscending =
        currentOrderBy.orderBy === field && currentOrderBy.orderDirection === OrderDirection.asc;
    return {
        orderBy: field,
        orderDirection: isActiveColumnAscending ? OrderDirection.desc : OrderDirection.asc,
    };
};

export const ACTION_PRICE_LIST_SORT_FIELDS = {
    CATEGORY: 'category',
    ACTION_CODE: 'actionCode',
    GROUPS: 'groups',
};

// Same order as the exported PDF: category, then code.
export const DEFAULT_ACTION_PRICE_LIST_ORDER: OrderBy = {
    orderBy: ACTION_PRICE_LIST_SORT_FIELDS.CATEGORY,
    orderDirection: OrderDirection.asc,
};

const selectActionCode: TextSelector<PriceListAction> = (action) => action.actionCode;
const selectActionCategory: TextSelector<PriceListAction> = (action) => action.category;
const selectActionDescription: TextSelector<PriceListAction> = (action) => action.description;
const selectActionGroupNames: TextSelector<PriceListAction> = (action) =>
    action.groups
        ?.filter(Boolean)
        .map((group) => group.name)
        .join(' | ');

const ACTION_SORT_SELECTORS: Record<string, TextSelector<PriceListAction>[]> = {
    [ACTION_PRICE_LIST_SORT_FIELDS.CATEGORY]: [selectActionCategory, selectActionCode, selectActionDescription],
    [ACTION_PRICE_LIST_SORT_FIELDS.ACTION_CODE]: [selectActionCode, selectActionDescription],
    [ACTION_PRICE_LIST_SORT_FIELDS.GROUPS]: [selectActionGroupNames, selectActionCode, selectActionDescription],
};

export const sortPriceListActions = (actions: PriceListAction[], orderBy: OrderBy): PriceListAction[] =>
    sortRows(
        actions,
        ACTION_SORT_SELECTORS[orderBy.orderBy] ?? ACTION_SORT_SELECTORS[DEFAULT_ACTION_PRICE_LIST_ORDER.orderBy],
        orderBy.orderDirection,
    );

export const ACTION_BASED_SORT_FIELDS = {
    MAIN_CATEGORY: 'mainCategory',
    SUB_CATEGORY: 'subCategory',
    NUMBER: 'number',
};

// Same order as the action based client price list PDF: main category, then code.
export const DEFAULT_ACTION_BASED_ORDER: OrderBy = {
    orderBy: ACTION_BASED_SORT_FIELDS.MAIN_CATEGORY,
    orderDirection: OrderDirection.asc,
};

const selectActionBasedNumber: TextSelector<ActionPrice> = (action) => action.number;
const selectActionBasedName: TextSelector<ActionPrice> = (action) => action.name;
const selectActionBasedMainCategory: TextSelector<ActionPrice> = (action) => action.mainCategory;
const selectActionBasedSubCategories: TextSelector<ActionPrice> = (action) => action.subCategory?.join(' ');

const ACTION_BASED_SORT_SELECTORS: Record<string, TextSelector<ActionPrice>[]> = {
    [ACTION_BASED_SORT_FIELDS.MAIN_CATEGORY]: [
        selectActionBasedMainCategory,
        selectActionBasedNumber,
        selectActionBasedName,
    ],
    [ACTION_BASED_SORT_FIELDS.SUB_CATEGORY]: [
        selectActionBasedSubCategories,
        selectActionBasedNumber,
        selectActionBasedName,
    ],
    [ACTION_BASED_SORT_FIELDS.NUMBER]: [selectActionBasedNumber, selectActionBasedName],
};

export const sortActionBasedActions = (actions: ActionPrice[], orderBy: OrderBy): ActionPrice[] =>
    sortRows(
        actions,
        ACTION_BASED_SORT_SELECTORS[orderBy.orderBy] ?? ACTION_BASED_SORT_SELECTORS[DEFAULT_ACTION_BASED_ORDER.orderBy],
        orderBy.orderDirection,
    );

export const MATERIAL_PRICE_LIST_SORT_FIELDS = {
    MATERIAL_CODE: 'materialCode',
    DESCRIPTION: 'description',
};

// Same order as the exported PDF: code.
export const DEFAULT_MATERIAL_PRICE_LIST_ORDER: OrderBy = {
    orderBy: MATERIAL_PRICE_LIST_SORT_FIELDS.MATERIAL_CODE,
    orderDirection: OrderDirection.asc,
};

const selectMaterialCode: TextSelector<PriceListMaterial> = (material) => material.materialCode;
const selectMaterialDescription: TextSelector<PriceListMaterial> = (material) => material.description;

const MATERIAL_SORT_SELECTORS: Record<string, TextSelector<PriceListMaterial>[]> = {
    [MATERIAL_PRICE_LIST_SORT_FIELDS.MATERIAL_CODE]: [selectMaterialCode, selectMaterialDescription],
    [MATERIAL_PRICE_LIST_SORT_FIELDS.DESCRIPTION]: [selectMaterialDescription, selectMaterialCode],
};

export const sortPriceListMaterials = (materials: PriceListMaterial[], orderBy: OrderBy): PriceListMaterial[] =>
    sortRows(
        materials,
        MATERIAL_SORT_SELECTORS[orderBy.orderBy] ?? MATERIAL_SORT_SELECTORS[DEFAULT_MATERIAL_PRICE_LIST_ORDER.orderBy],
        orderBy.orderDirection,
    );
